import { Box, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import moment from 'moment';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { THandleAction } from 'services/constants/tableStructureTypes';
import { PAGE_SIZE } from 'services/constants/env';
import { COLORS } from 'services/constants/colors';
import { getPpwrManualReportingRequest } from 'redux/reducers/ppwr/reducer';
import {
	getPpwrManualReporting,
	getPpwrManualReportingIsLoading,
} from 'redux/reducers/ppwr/selectors';
import { TPpwrManualReportingCountry } from 'redux/reducers/ppwr/types';
import { ItemWithId } from 'services/constants/types';
import { PPWR_MANUAL_REPORT_ACTIONS, tableStructure } from './tableStructure';
import { EditParcelsCountDialog } from './EditParcelsCountDialog';

type TPpwrManualReportTableRow = ItemWithId &
	Omit<TPpwrManualReportingCountry, 'updatedBy'> & { updatedBy: string };

const ROWS = Array.from(new Array(5));
const PERIOD_FORMAT = 'DD.MM.YYYY';

export const PpwrManualReport: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();
	const { page } = queryString.parse(search);
	const manualReporting = useSelector(getPpwrManualReporting);
	const manualReportingIsLoading = useSelector(getPpwrManualReportingIsLoading);

	useEffect(() => {
		dispatch(
			getPpwrManualReportingRequest({
				pageNumber: page ? +page : 1,
				pageSize: 100,
				filters: [],
				orderBy: [],
				searchString: '',
			}),
		);
	}, [dispatch, page]);

	const easTableItems = useMemo(() => {
		const rows: TPpwrManualReportTableRow[] = (manualReporting?.data ?? []).map((item, idx) => ({
			id: idx,
			countryCode: item.countryCode,
			parcelsCount: item.parcelsCount ?? 0,
			updatedAt: item.updatedAt,
			updatedBy: item.updatedBy?.fullName ?? item.updatedBy?.email ?? '',
		}));
		return { data: rows, totalCount: manualReporting?.totalCount ?? rows.length };
	}, [manualReporting]);

	const reportingPeriod =
		manualReporting?.reportingPeriodStart && manualReporting?.reportingPeriodEnd
			? `${moment(manualReporting.reportingPeriodStart).format(PERIOD_FORMAT)} – ${moment(
					manualReporting.reportingPeriodEnd,
				).format(PERIOD_FORMAT)}`
			: null;

	// Index (within the current page) of the row edited in the dialog.
	const [editIndex, setEditIndex] = useState<number | null>(null);

	const handleAction: THandleAction = (id, type) => {
		if (type === PPWR_MANUAL_REPORT_ACTIONS.EDIT) setEditIndex(id);
	};

	return (
		<>
			<EasTableWithoutFilter<TPpwrManualReportTableRow>
				tableStructure={tableStructure}
				items={easTableItems}
				itemsIsLoading={manualReportingIsLoading}
				handleAction={handleAction}
				skeletonRows={ROWS}
				highlightedRow={null}
				pageSize={100}
			>
				<Box
					sx={{
						p: '2px 4px',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography sx={{ fontSize: '1.2rem', color: COLORS.PRIMARY, fontWeight: 'bold' }}>
						ERP/PPWR Filing Manual Reports
					</Typography>
					{reportingPeriod && (
						<Typography sx={{ fontSize: '1rem', color: COLORS.PRIMARY }}>
							Reporting period: <b>{reportingPeriod}</b>
						</Typography>
					)}
				</Box>
			</EasTableWithoutFilter>
			<EditParcelsCountDialog
				rows={manualReporting?.data ?? []}
				index={editIndex}
				setIndex={setEditIndex}
				onClose={() => setEditIndex(null)}
			/>
		</>
	);
};
